/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import api from '@/lib/api';
import PostCard from '@/components/PostCard';
import PostComposer from '@/components/PostComposer';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PostSkeleton, EmptyState } from '@/components/UIExtras';
import { PenSquare } from 'lucide-react';
import { PROFESSIONAL_CATEGORIES } from '@/lib/utils-cat';
import TodayHighlights from '@/components/TodayHighlights';
import DailyBriefingCard from '@/components/DailyBriefingCard';
import { OnboardingBanner } from '@/components/OnboardingCTA';
import RedeemInviteBanner from '@/components/RedeemInviteBanner';

const FeedPage = () => {
  const { t } = useTranslation();
  const [scope, setScope] = useState('general');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (s) => {
    setLoading(true);
    try {
      const res = await api.get(`/posts?scope=${s}`);
      setPosts(res.data || []);
    } catch (e) {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(scope); }, [scope, load]);

  const handleCreated = (newPost) => {
    setPosts((prev) => [newPost, ...prev]);
  };

  const handleChanged = (updated) => {
    setPosts((prev) => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
  };

  const handleDeleted = (id) => {
    setPosts((prev) => prev.filter(p => p.id !== id));
  };

  return (
    <div className="space-y-4">
      <RedeemInviteBanner />
      <OnboardingBanner />

      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="flex items-center justify-between gap-2"
      >
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{t('feed')}</h1>
          <p className="text-sm text-muted-foreground">{t('shareIdea')}</p>
        </div>
        <PostComposer onCreated={handleCreated} />
      </motion.div>

      <Tabs value={scope} onValueChange={setScope} className="w-full">
        <TabsList className="w-full flex flex-wrap justify-start gap-1 bg-secondary/60 p-1 rounded-full h-auto" data-testid="feed-tabs">
          <TabsTrigger value="general" data-testid="feed-tab-general" className="rounded-full text-xs">
            {t('generale')}
          </TabsTrigger>
          {PROFESSIONAL_CATEGORIES.map(([key, meta]) => {
            const Icon = meta.icon;
            return (
              <TabsTrigger key={key} value={key} data-testid={`feed-tab-${key}`} className="rounded-full text-xs gap-1">
                <Icon className="h-3.5 w-3.5" />
                {t(meta.label)}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <AnimatePresence mode="wait">
        {scope === 'general' && (
          <motion.div
            key="general-hero"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
          >
            <DailyBriefingCard />
            <TodayHighlights />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {loading && (
          <>
            <PostSkeleton />
            <PostSkeleton />
          </>
        )}
        {!loading && posts.length === 0 && (
          <EmptyState
            icon={<PenSquare className="h-6 w-6" />}
            title={t('noPostsYet')}
            subtitle={t('beFirstToPost')}
            action={<PostComposer onCreated={handleCreated} />}
          />
        )}
        <AnimatePresence initial={false}>
          {!loading && posts.map((p, i) => (
            <motion.div
              key={p.id}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.28, delay: Math.min(i, 5) * 0.04, ease: 'easeOut' }}
            >
              <PostCard post={p} onChanged={handleChanged} onDeleted={handleDeleted} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FeedPage;
